import { UTCDate } from '@date-fns/utc';
import { AccountView, TransactionView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { ValueTone } from '../components/metrics/metric-card';
import { getHistoricalCashBalance } from './get-historical-cash-balance';
import { isInternalTransfer } from './is-internal-transfer';
import { isRefund } from './is-refund';

type MonthlyBurnDataInput = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
  currentPeriodStartDate: Date;
  currentPeriodEndDate: Date;
  previousPeriodStartDate: Date;
  previousPeriodEndDate: Date;
};

type TrendDirection = 'increasing' | 'decreasing' | 'default';

const EPSILON = 0.01; // Small threshold to avoid noisy percentage swings

/**
 * Computes burn as total outflows (negative transactions) in the given timeframe.
 * Returns formatted amount, period-over-period percentage change based on magnitude, and a trend direction.
 */
export const getMonthlyBurnData = ({
  accounts,
  baseCurrency,
  currentPeriodStartDate,
  currentPeriodEndDate,
  previousPeriodStartDate,
  previousPeriodEndDate,
}: MonthlyBurnDataInput) => {
  if (!accounts || accounts.length === 0) {
    return {
      formattedMonthlyBurn: getAmountWithCurrency({ amount: 0, currency: baseCurrency, isAccountBalance: true }),
      percentageChange: '0%',
      trend: 'default' as TrendDirection,
      valueTone: 'neutral' as const,
    };
  }

  const beginningOfMonthCashData = getHistoricalCashBalance({
    accounts,
    baseCurrency,
    targetDate: currentPeriodStartDate,
  });

  const allTransactions = accounts.flatMap((account) =>
    (account.transactions || []).map(({ fspDate, baseCurrencyAmount, type, isCredit, vendor }) => ({
      fspDate,
      baseCurrencyAmount,
      type,
      isCredit,
      vendor,
    })),
  );

  // Use explicit period boundaries provided by the caller
  const currentStart = new UTCDate(currentPeriodStartDate);
  const currentEnd = new UTCDate(currentPeriodEndDate);
  const prevStart = new UTCDate(previousPeriodStartDate);
  const prevEnd = new UTCDate(previousPeriodEndDate);

  const inRange = ({ date, start, end }: { date: Date; start: Date; end: Date }) => date >= start && date <= end;

  const computePeriodBurn = (start: Date, end: Date) => {
    const periodTransactions = allTransactions
      .filter((t) => (t.fspDate ? inRange({ date: new UTCDate(t.fspDate), start, end }) : false))
      .filter((t) => !isInternalTransfer(t as TransactionView));

    // Calculate expenses (debits) minus refunds (credits that are refunds)
    const expenses = periodTransactions
      .filter((t) => !t.isCredit)
      .reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount || 0), 0);

    const refunds = periodTransactions
      .filter((t) => t.isCredit && isRefund(t.type))
      .reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount || 0), 0);

    // Return negative value (expenses minus refunds)
    return -(expenses - refunds);
  };

  // Current period: expenses minus refunds (refunds reduce burn)
  const currentPeriodBurn = computePeriodBurn(currentStart, currentEnd);

  const formattedMonthlyBurn = getAmountWithCurrency({
    amount: currentPeriodBurn,
    currency: baseCurrency,
    // Use account-balance formatting to preserve the minus sign for negative burn values
    isAccountBalance: true,
  });

  // Previous period of equal length immediately before the current range
  const previousPeriodBurn = computePeriodBurn(prevStart, prevEnd);

  const currentAbs = Math.abs(currentPeriodBurn);
  const previousAbs = Math.abs(previousPeriodBurn);

  let percentageChange = '0%';
  let trend: TrendDirection = 'default';

  if (previousAbs < EPSILON) {
    if (currentAbs < EPSILON) {
      percentageChange = '0%';
      trend = 'default';
    } else {
      // Jump from ~0 to some burn: treat as 100% worse
      percentageChange = '100%';
      trend = 'decreasing';
    }
  } else {
    const delta = currentAbs - previousAbs; // + means worse (more burn), - means better (less burn)
    const percent = Math.abs((delta / previousAbs) * 100);
    percentageChange = `${percent.toFixed(1)}%`;

    if (Math.abs(delta) < EPSILON) {
      trend = 'default';
    } else {
      trend = delta > 0 ? 'increasing' : 'decreasing';
    }
  }

  let valueTone: ValueTone;
  switch (trend) {
    case 'increasing':
      valueTone = 'negative' as const; // increase burn is negative tone because it's a bad thing
      break;
    case 'decreasing':
      valueTone = 'positive' as const; // decrease burn is positive tone because it's a good thing
      break;
    default:
      valueTone = 'neutral' as const;
      break;
  }

  const burnRateInPercentage = beginningOfMonthCashData.historicalBalanceInBaseCurrency
    ? (currentPeriodBurn / beginningOfMonthCashData.historicalBalanceInBaseCurrency) * 100
    : 0;
  const absoluteChangeInBurn = getAmountWithCurrency({
    amount: currentPeriodBurn - previousPeriodBurn,
    currency: baseCurrency,
    isAccountBalance: true,
  });

  return {
    burnRateInPercentage: `${burnRateInPercentage.toFixed(1)}%`,
    absoluteChangeInBurn,
    monthlyBurnInBaseCurrency: currentPeriodBurn,
    formattedMonthlyBurn,
    percentageChange,
    trend,
    valueTone,
  };
};
